<script setup>
import { computed, onUnmounted, reactive, ref, watch, watchEffect } from 'vue'
import { useStore } from 'vuex'

import { GripVertical } from 'lucide-vue-next'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import {
  BASEMAPS,
  BLANK_BASEMAP,
  BLANK_BASEMAP_KEY,
  DEFAULT_BASEMAP_KEY,
  getBasemap,
  applyBasemap,
  applyLabels,
  applyBuildings,
  applyTerrain,
  latestPlanetscopeSelection,
  planetscopeMosaicTiles
} from '@/components/maps/basemaps'
import PlanetscopeBasemapControls from '@/components/maps/PlanetscopeBasemapControls.vue'

// Draggable basemap stack: list order = render order (top row renders above).
// The topmost enabled Mapbox-style entry becomes the map's style; enabled
// raster/PlanetScope entries render as `bm-*` raster layers stacked above the
// style but below the z-layer anchors (so all data layers stay on top).

const props = defineProps({
  // Per-tile usage passes the live Mapbox instance + its map object so each
  // tile drives its own stack; the global canvas falls back to geo/getMap.
  instance: { type: Object, default: null },
  map: { type: Object, default: null },
  orgId: { type: [Number, String], default: null }
})

const store = useStore()
const mapInstance = computed(() => props.instance ?? store.getters['geo/getMap'])
const mapReady = computed(() => (props.instance ? true : store.getters['geo/getMapReady']))
const orgId = computed(() => props.orgId ?? store.getters['org/getSelected'])
const planetAccess = computed(() => store.getters['geo/getIsPlanetscopeAccess'])
const psMapId = computed(() => props.map?.id ?? '__global__')

// --- Stack state (persisted per org, and per map on split-screen tiles) --------
const storageKey = computed(() =>
  `kabini.basemapStack.${orgId.value}${props.map ? `.${props.map.id}` : ''}`
)

const stack = ref([])
const psSelection = ref(null)
const hasSavedStack = ref(false)

// The basemap the map object asks for. The global canvas has no `map` prop and
// follows the org's default map instead (same object MainMap renders).
const mapBasemap = computed(() =>
  props.map?.basemap ?? store.getters['maps/getDefaultMap'](orgId.value)?.basemap
)

const defaultStack = () => {
  const initial = getBasemap(mapBasemap.value ?? DEFAULT_BASEMAP_KEY).key
  return BASEMAPS.map((b) => ({ key: b.key, enabled: b.key === initial }))
}

const loadStack = () => {
  let saved = null
  try {
    saved = JSON.parse(localStorage.getItem(storageKey.value) || 'null')
  } catch (e) {
    saved = null
  }
  hasSavedStack.value = Array.isArray(saved?.stack)
  if (!hasSavedStack.value) {
    stack.value = defaultStack()
    psSelection.value = null
    return
  }
  const entries = saved.stack
    .filter((e) => BASEMAPS.some((b) => b.key === e.key))
    .map((e) => ({ key: e.key, enabled: !!e.enabled }))
  for (const b of BASEMAPS) {
    if (!entries.some((e) => e.key === b.key)) entries.push({ key: b.key, enabled: false })
  }
  stack.value = entries
  // The session store is empty on a fresh load unless a shared link's hash was
  // rehydrated into it, so a value there beats this browser's last local choice —
  // otherwise the picker would show the sharer's month while the map rendered
  // ours. With nothing to defer to, seed the store from localStorage so the
  // picker restores what's on screen.
  const shared = store.getters['layers/getPlanetscopeSelection'](psMapId.value)
  psSelection.value = shared ?? saved.planetscope ?? null
  if (psSelection.value && !shared) {
    store.dispatch('layers/setPlanetscopeSelection', { layerId: psMapId.value, selection: psSelection.value })
  }
}

const persistStack = () => {
  localStorage.setItem(storageKey.value, JSON.stringify({ stack: stack.value, planetscope: psSelection.value }))
}

const rows = computed(() => stack.value.filter((e) => e.key !== 'planetscope' || planetAccess.value))

const topEntry = computed(() => rows.value.find((e) => e.enabled))
const triggerBasemap = computed(() => (topEntry.value ? getBasemap(topEntry.value.key) : BLANK_BASEMAP))

// --- Applying the stack to the Mapbox instance ---------------------------------
const appliedStyleKey = ref(null)
const srcTiles = new Map()
let attachedMap = null

// The style key the canvas booted with, so the first apply doesn't trigger a
// redundant setStyle (which races draw-layer raising). MapCanvas creates with the
// blank style for any non-Mapbox basemap, since those render as `bm-*` layers.
const bootBasemapKey = () => {
  const bm = getBasemap(mapBasemap.value)
  return bm.kind === 'mapbox' ? bm.key : BLANK_BASEMAP_KEY
}

const topMapboxKey = () =>
  stack.value.find((e) => e.enabled && getBasemap(e.key).kind === 'mapbox')?.key ?? null

const rasterTilesFor = (key) => {
  const bm = getBasemap(key)
  if (bm.kind === 'raster') return bm.tiles
  if (bm.kind === 'planetscope' && orgId.value && psSelection.value?.name) {
    return planetscopeMosaicTiles(Number(orgId.value), psSelection.value.name)
  }
  return null
}

// PlanetScope needs a resolved mosaic name before it can render. The month picker
// supplies one, but it lives inside the popover and doesn't mount until the user
// opens it — so resolve the latest monthly mosaic here instead of rendering
// nothing until someone goes looking for the selector.
const psEnabled = computed(() => stack.value.some((e) => e.key === 'planetscope' && e.enabled))

let psRequest = null

const ensurePlanetscopeSelection = async () => {
  if (!psEnabled.value || !orgId.value || psSelection.value?.name) return
  const token = storageKey.value
  psRequest = token
  const selection = await latestPlanetscopeSelection(Number(orgId.value))
  // Bail if the org/map switched mid-flight, or the picker resolved first.
  if (!selection || psRequest !== token || psSelection.value?.name) return
  psSelection.value = selection
  persistStack()
  // The picker, the share-link hash and URL rehydration all read this.
  store.dispatch('layers/setPlanetscopeSelection', { layerId: psMapId.value, selection })
  applyRasters()
}

const applyRasters = () => {
  const map = mapInstance.value
  if (!map || !map.isStyleLoaded()) return
  const style = map.getStyle()
  if (!style) return
  for (const layer of style.layers ?? []) {
    if (layer.id.startsWith('bm-')) map.removeLayer(layer.id)
  }
  // Insert below the bottom z-layer anchor so every data layer renders above
  // the basemap rasters. Reverse order: the topmost stack entry is added last,
  // landing highest under the anchor.
  const anchor = (style.layers ?? []).find((l) => /^z-\d+$/.test(l.id))?.id
  for (const entry of [...stack.value].reverse()) {
    if (!entry.enabled) continue
    const tiles = rasterTilesFor(entry.key)
    if (!tiles) continue
    const srcId = `bm-src-${entry.key}`
    const tilesKey = tiles.join('|')
    if (map.getSource(srcId) && srcTiles.get(srcId) !== tilesKey) map.removeSource(srcId)
    if (!map.getSource(srcId)) {
      map.addSource(srcId, { type: 'raster', tiles, tileSize: 256 })
      srcTiles.set(srcId, tilesKey)
    }
    map.addLayer({ id: `bm-${entry.key}`, type: 'raster', source: srcId }, anchor)
  }
}

const applyStack = () => {
  const map = mapInstance.value
  if (!map || !mapReady.value) return
  // No Mapbox style enabled means the base is empty — keeping the last applied
  // style would leave its roads and labels showing under (or instead of) the
  // stack, so switching everything off would never blank the map.
  const styleKey = topMapboxKey() ?? BLANK_BASEMAP_KEY
  if (styleKey !== appliedStyleKey.value) {
    appliedStyleKey.value = styleKey
    // setStyle wipes everything; the style.load handler re-adds the rasters.
    applyBasemap(map, styleKey)
    return
  }
  applyRasters()
}

const onStackChanged = () => {
  persistStack()
  applyStack()
}

const toggleEntry = (key) => {
  stack.value = stack.value.map((e) => (e.key === key ? { ...e, enabled: !e.enabled } : e))
  onStackChanged()
}

const moveEntry = (sourceKey, targetKey) => {
  if (sourceKey === targetKey) return
  const next = [...stack.value]
  const from = next.findIndex((e) => e.key === sourceKey)
  const to = next.findIndex((e) => e.key === targetKey)
  if (from === -1 || to === -1) return
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  stack.value = next
  onStackChanged()
}

const onPlanetscopeChange = (selection) => {
  psSelection.value = selection
  persistStack()
  applyRasters()
}

const isSatelliteStyle = () => getBasemap(appliedStyleKey.value).label === 'Satellite'

const onStyleLoad = () => {
  srcTiles.clear()
  applyRasters()
  // MapCanvas re-applies view toggles keyed by the map object's basemap; when
  // the stack owns the style that key can mismatch, so restore local state too.
  const map = mapInstance.value
  if (!map) return
  if (!isLabels.value) applyLabels(map, false, isSatelliteStyle())
  if (isBuildings.value) applyBuildings(map, true)
  if (isTerrain.value) applyTerrain(map, true)
}

// One watcher, so appliedStyleKey is always seeded from the style the canvas
// booted with before the first applyStack() — two separate immediate watchers
// left the first apply running against a null key and issuing a setStyle the
// second one then contradicted.
watch([mapInstance, mapReady, storageKey], ([instance, ready], prev) => {
  if (storageKey.value !== prev?.[2]) loadStack()
  if (attachedMap && attachedMap !== instance) {
    attachedMap.off('style.load', onStyleLoad)
    attachedMap = null
  }
  if (!instance || !ready) return
  if (attachedMap !== instance) {
    attachedMap = instance
    appliedStyleKey.value = bootBasemapKey()
    attachedMap.on('style.load', onStyleLoad)
  }
  applyStack()
}, { immediate: true })

watch([psEnabled, orgId, storageKey], ensurePlanetscopeSelection, { immediate: true })

// The map's own basemap is set from the create/edit form and from shared links,
// so mirror a change into the stack: enable the new entry and switch off the one
// it replaces, leaving any extra rasters the user stacked on top alone.
watch(mapBasemap, (key, prev) => {
  if (!key || key === prev) return
  // No previous value means the map object only just resolved (the global canvas
  // mounts before the org's maps load). Adopt it as the default, but never over a
  // stack the user has already arranged.
  if (!prev) {
    if (hasSavedStack.value) return
    stack.value = defaultStack()
    onStackChanged()
    return
  }
  const next = getBasemap(key).key
  const previous = getBasemap(prev).key
  stack.value = stack.value.map((e) => {
    if (e.key === next) return { ...e, enabled: true }
    if (e.key === previous) return { ...e, enabled: false }
    return e
  })
  onStackChanged()
})

// --- Drag to reorder (pragmatic-drag-and-drop, as in LayerList/LayerItem) ------
const dndToken = { id: Symbol('basemap-stack') }
const rowEls = reactive({})
const draggingKey = ref(null)
const overKey = ref(null)

watchEffect((onCleanup) => {
  const cleanups = []
  for (const entry of rows.value) {
    const el = rowEls[entry.key]
    if (!el) continue
    const key = entry.key
    cleanups.push(combine(
      draggable({
        element: el,
        getInitialData: () => ({ token: dndToken, key }),
        onDragStart: () => { draggingKey.value = key },
        onDrop: () => { draggingKey.value = null }
      }),
      dropTargetForElements({
        element: el,
        getData: () => ({ token: dndToken, key }),
        canDrop: ({ source }) => source.data.token === dndToken && source.data.key !== key,
        onDragEnter: () => { overKey.value = key },
        onDragLeave: () => { overKey.value = null },
        onDrop: () => { overKey.value = null }
      })
    ))
  }
  onCleanup(() => cleanups.forEach((c) => c()))
})

// The monitor is global — the token scopes drops to this panel instance.
const monitorCleanup = monitorForElements({
  onDrop({ source, location }) {
    const target = location.current.dropTargets[0]
    if (!target || source.data.token !== dndToken || target.data.token !== dndToken) return
    moveEntry(source.data.key, target.data.key)
  }
})

onUnmounted(() => {
  monitorCleanup()
  if (attachedMap) attachedMap.off('style.load', onStyleLoad)
})

// --- Labels / 3D view toggles ---------------------------------------------------
const isLabels = ref(true)
const is3DView = ref(false)
const isBuildings = ref(false)
const isTerrain = ref(false)

const persistViewSettings = () => {
  if (!props.map) return
  store.dispatch('layers/setMapViewSettings', {
    mapId: props.map.id,
    settings: { labels: isLabels.value, buildings: isBuildings.value, terrain: isTerrain.value }
  })
}

const switchLabels = () => {
  isLabels.value = !isLabels.value
  applyLabels(mapInstance.value, isLabels.value, isSatelliteStyle())
  persistViewSettings()
}

const switchBuildings = () => {
  isBuildings.value = !isBuildings.value
  applyBuildings(mapInstance.value, isBuildings.value)
  persistViewSettings()
}

const switchTerrain = () => {
  isTerrain.value = !isTerrain.value
  applyTerrain(mapInstance.value, isTerrain.value)
  persistViewSettings()
}

const toggleMapDimension = () => {
  is3DView.value = !is3DView.value
  mapInstance.value?.easeTo({ pitch: is3DView.value ? 75 : 0, bearing: 0, duration: 0 })
}

watch(() => (props.map?.id ? store.getters['layers/getMapViewSettings'](props.map.id) : null), (saved) => {
  isLabels.value = saved?.labels !== false
  isBuildings.value = !!saved?.buildings
  isTerrain.value = !!saved?.terrain
  is3DView.value = false
}, { immediate: true, deep: true })
</script>

<template>
  <Popover v-if="mapInstance && mapReady">
    <PopoverTrigger as-child>
      <img
        :src="triggerBasemap.preview"
        class="object-cover cursor-pointer"
        :class="map
          ? 'h-full aspect-square'
          : [
              'w-10 h-10 rounded border-2 shadow-lg',
              triggerBasemap.theme === 'light' ? 'border-white' : 'border-gray-700'
            ]"
      />
    </PopoverTrigger>
    <PopoverContent class="p-2 w-72 text-sm max-h-[75vh] overflow-y-auto">
      <div class="px-1 pb-0.5 font-semibold">Basemaps</div>
      <div class="px-1 pb-2 text-xs text-muted-foreground">Drag to reorder · top renders above</div>

      <template v-for="entry in rows" :key="entry.key">
        <div
          :ref="(el) => { rowEls[entry.key] = el }"
          class="flex items-center gap-2 rounded-md p-1.5 hover:bg-muted/60"
          :class="{ 'ring-1 ring-primary/60': overKey === entry.key, 'opacity-50': draggingKey === entry.key }"
        >
          <GripVertical class="w-4 h-4 shrink-0 cursor-grab text-muted-foreground" />
          <img
            :src="getBasemap(entry.key).preview"
            class="w-9 h-9 shrink-0 rounded border-2 object-cover"
            :class="entry.enabled ? 'border-primary' : 'border-gray-300 opacity-60'"
          />
          <span
            class="min-w-0 flex-1 truncate"
            :class="entry.enabled ? 'font-medium' : 'text-muted-foreground'"
          >{{ getBasemap(entry.key).label }}</span>
          <Switch
            :model-value="entry.enabled"
            @update:model-value="toggleEntry(entry.key)"
          />
        </div>

        <div v-if="entry.key === 'planetscope' && entry.enabled && orgId" class="pl-7 pr-1 pb-1.5">
          <PlanetscopeBasemapControls
            :org-id="orgId"
            :map-id="psMapId"
            @change="onPlanetscopeChange"
          />
        </div>
      </template>

      <hr class="my-2">

      <div class="space-y-2 px-1">
        <div class="flex items-center">
          <Switch :model-value="isLabels" @update:model-value="switchLabels" />
          <Label class="ml-2">Labels</Label>
        </div>
        <div class="flex items-center">
          <Switch :model-value="is3DView" @update:model-value="toggleMapDimension" />
          <Label class="ml-2">3D View</Label>
        </div>
        <div class="flex items-center">
          <Switch :model-value="isBuildings" @update:model-value="switchBuildings" />
          <Label class="ml-2">3D Buildings</Label>
        </div>
        <div class="flex items-center">
          <Switch :model-value="isTerrain" @update:model-value="switchTerrain" />
          <Label class="ml-2">3D Terrain</Label>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
